import type {
  AnswerStatus,
  SurveyAnswerRecord,
  SurveyConfig,
} from '@coorum-research/shared-common/types'
import type { CSSProperties } from 'react'
import { parseAnswer } from '@coorum-research/shared-common/utils/answer.util'
import { formatDate } from '@coorum-research/shared-common/utils/date.util'
import { flattenPanelAnswers } from '@coorum-research/shared-common/utils/question-table-data.util'
import { clsx } from 'clsx/lite'
import { useMemo } from 'react'
import { FileAnswer } from './answers-list-item/FileAnswer'
import { ImagePickerAnswer } from './answers-list-item/ImagePickerAnswer'
import { RatingAnswer } from './answers-list-item/RatingAnswer'
import { EditIcon } from './icons/EditIcon'
import { useTheme } from './ThemeContext'

const itemStatusStyles: Record<AnswerStatus, CSSProperties> = {
  draft: {
    backgroundColor: '#D81B60',
    color: 'white',
  },
  done: {
    backgroundColor: '#5C68C0',
    color: 'white',
  },
}

type Props = {
  item: {
    id: string
    surveyName: string | null
    answerStatus: AnswerStatus
    createdAt: string
    surveyAnswer?: string | null
    surveyAnswerJson?: SurveyAnswerRecord | null
    mypageDisplayQuestionId?: string | null
    surveyConfig?: string | null
  }
  className?: string
  onItemClick?: (item: any) => void
  onIconClick?: (item: any) => void
  isFirst?: boolean
}

export function AnswersListItem({ item, className, onItemClick, onIconClick, isFirst }: Props) {
  const themeJson = useTheme()

  const handleItemClick = () => {
    if (onItemClick) {
      onItemClick(item)
    }
  }

  const handleIconClick = (ev: React.MouseEvent<HTMLElement>) => {
    ev.stopPropagation()

    if (onIconClick) {
      onIconClick(item)
    }
  }

  // surveyConfigをメモ化
  const memoizedSurveyConfig = useMemo(() => {
    if (!item.surveyConfig) {
      return null
    }

    try {
      return JSON.parse(item.surveyConfig) as SurveyConfig
    } catch (error) {
      console.error('Error parsing survey config:', error)
      return null
    }
  }, [item.surveyConfig])

  // NOTE: useMemoしてもしなくても同じかな
  const memoizedSurveyAnswerJson = useMemo(() => {
    return item.surveyAnswerJson
  }, [item.surveyAnswerJson])

  const displayContent = useMemo(() => {
    if (!memoizedSurveyConfig || !memoizedSurveyAnswerJson) {
      return item.surveyName || ''
    }

    // mypageDisplayQuestionIdから対象の質問要素を探す
    // panel内の質問の場合は、panel要素とその中の質問要素を探す
    let targetQuestionElement = memoizedSurveyConfig.elements?.find(
      element => element.name === item.mypageDisplayQuestionId,
    )

    // 直接の要素で見つからない場合は、panel内を探す
    if (!targetQuestionElement) {
      for (const element of memoizedSurveyConfig.elements || []) {
        if (element.type === 'panel' && element.elements) {
          const panelQuestionElement = element.elements.find(
            e => e.name === item.mypageDisplayQuestionId,
          )
          if (panelQuestionElement) {
            targetQuestionElement = panelQuestionElement
            break
          }
        }
      }
    }

    if (!targetQuestionElement) {
      return item.surveyName || ''
    }

    // 画像選択
    if (targetQuestionElement.type === 'imagepicker') {
      return (
        <ImagePickerAnswer
          questionElement={targetQuestionElement}
          answerValue={memoizedSurveyAnswerJson[targetQuestionElement.name]}
        />
      )
    }

    const memoizedSurveyFlattenedAnswer = flattenPanelAnswers(memoizedSurveyAnswerJson ?? {})
    const parsedAnswer = parseAnswer(targetQuestionElement, memoizedSurveyFlattenedAnswer)
    if (!parsedAnswer) {
      return ''
    }

    // 画像アップロード
    if (targetQuestionElement.type === 'file') {
      return <FileAnswer answer={parsedAnswer} />
    }

    // 星評価
    if (targetQuestionElement.type === 'rating') {
      return (
        <RatingAnswer
          questionElement={targetQuestionElement}
          answer={parsedAnswer}
          themeColor={themeJson.mainColor}
        />
      )
    }

    // その他の設問タイプの場合はテキストを表示
    if (Array.isArray(parsedAnswer)) {
      return parsedAnswer[0]?.text || ''
    }

    return parsedAnswer.text || ''
  }, [memoizedSurveyConfig, memoizedSurveyAnswerJson, item.surveyName, item.mypageDisplayQuestionId, themeJson.mainColor])

  return (
    <>
      <div className={clsx('mx-4 border-t', isFirst ? 'border-t-transparent' : 'border-t-[#E2E3E2]')} />
      <div className={clsx('grid cursor-pointer grid-cols-[50px,1fr,24px] grid-rows-[auto_4px_auto] gap-x-2 px-4 py-3 text-sm hover:opacity-80', className)} key={item.id} onClick={handleItemClick}>
        <div className="col-span-1 row-span-1 flex w-full items-center justify-center rounded-sm text-xs font-bold" style={itemStatusStyles[item.answerStatus]}>{item.answerStatus === 'done' ? '完了' : '中断'}</div>
        <div className="col-span-1 col-start-2 row-span-1">{formatDate(item.createdAt, 'yyyy/MM/dd')}</div>
        <div className={clsx('row-span-1 row-start-3 truncate', onIconClick ? 'col-span-2' : 'col-span-3')}>
          {displayContent}
        </div>
        {onIconClick
          && (
            <div className="col-start-3 row-span-3 flex size-full cursor-pointer items-center justify-center" onClick={handleIconClick}>
              <EditIcon />
            </div>
          )}
      </div>
    </>
  )
}
